import React, { useState, useMemo } from 'react';
import InfoStep from './steps/InfoStep';
import HierarchyStep from './steps/HierarchyStep';
import SafetyQuestionsStep from './steps/SafetyQuestionsStep';
// import LOTOStep from './steps/LOTOStep'; // REMOVED
import PersonnelStep from './steps/PersonnelStep';
import SummaryStep from './steps/SummaryStep';
import { getCombinedData } from '../utils/conditionalLogic';

import ToolsPPEStep from './steps/ToolsPPEStep';

const STEPS = [
    { title: 'Info', component: InfoStep },
    { title: 'Locatie & Veiligheid', component: HierarchyStep }, // Merged Step
    { title: 'Gereedschap & PBM', component: ToolsPPEStep },
    { title: 'Veiligheid', component: SafetyQuestionsStep },
    // { title: 'LOTO', component: LOTOStep }, // REMOVED (Merged into HierarchyStep)
    { title: 'Personeel', component: PersonnelStep },
    { title: 'Overzicht', component: SummaryStep },
];

export default function Wizard({ data, locationName, currentUser, initialData, availableLocations, onUpdateLocation, onPersist, persistedState, onSubmitSuccess }) {
    const [currentStep, setCurrentStep] = useState(0); // Navigation state is usually reset on resume, valid choice.

    // Initialize with persisted state if valid, else initialData (edit), else default
    const [formData, setFormData] = useState({
        location: locationName || '',
        ultimoNr: '',
        date: new Date().toISOString().split('T')[0],
        description: '',
        goal: '',
        hall: '',
        line: '',
        subline: '',
        component: '',
        task: '',
        safetyQuestions: {},
        lotoPhotos: [],
        werkvoorbereider: '',
        werkverantwoordelijke: '',
        monteur1: '',
        monteur2: '',
        risks: [],
        tools: [],
        pbms: [],
        assignedUserId: '',
        ...initialData,
        checks: initialData?.checks || []
    });

    // SYNC TO PARENT (Persistence) - Debounced
    React.useEffect(() => {
        if (!onPersist) return;

        const handler = setTimeout(() => {
            onPersist(formData);
        }, 500); // Debounce 500ms to allow render cycle to settle

        return () => clearTimeout(handler);
    }, [formData, onPersist]);

    const [newPhotoUploaded, setNewPhotoUploaded] = useState(false);

    const StepComponent = STEPS[currentStep].component;

    // Calculate combined data (merged hierarchy + library + conditional logic)


    // Validation Logic
    const canProceed = () => {
        if (currentStep === 0) return formData.ultimoNr && formData.date && formData.description && formData.goal;
        if (currentStep === 1) {
            // Step 2: Hierarchy & Task - Require at least one photo (Global Library)
            // HARD BLOCK: If any image is unnamed (formData.unnamedImages > 0), DO NOT PROCEED.
            // Note: lotoPhotos check is also covered here because they share the unnamedImages pool in HierarchyStep
            if (formData.unnamedImages && formData.unnamedImages.length > 0) return false;

            // Basic Hierarchy Checks
            if (!formData.hall || !formData.line || !formData.subline || !formData.component || !formData.task) return false;

            // Asset Photo Check
            if (!formData.taskImageUrls || formData.taskImageUrls.length === 0) return false;

            // LOTO Validation (Merged)
            if (combinedData.lotoRequired) {
                const requiredCount = combinedData.lotoSteps ? combinedData.lotoSteps.length : 0;
                const selectedCount = formData.lotoPhotos ? formData.lotoPhotos.length : 0;
                if (selectedCount < requiredCount) return false;
            }
            return true;
        }
        if (currentStep === 2) return true; // Tools & PPEs (Optional)
        if (currentStep === 3) return true; // Safety Questions (Optional)
        if (currentStep === 4) { // Personnel Step
            if (!formData.werkvoorbereider || !formData.werkverantwoordelijke || !formData.monteur1) return false;

            // --- SAFETY HARD BLOCK ---
            const riskLevel = formData.riskLevelOverride ? parseInt(formData.riskLevelOverride) : (combinedData?.riskLevel || 1);
            const user = (dynamicWizardData.users || []).find(u => u.username === formData.assignedUserId);
            if (!user) return true;

            const roleName = user.linkedPersonnelRoleId;
            if (!roleName) return false;

            const personnelList = dynamicWizardData.personnel || [];
            const personRole = personnelList.find(p => p.Functie === roleName);
            if (!personRole) return false;

            const check = (val) => {
                if (!val) return false;
                if (val === true) return true;
                if (typeof val === 'string') {
                    const s = val.trim();
                    return s === 'Ja' || s === 'x' || s === 'true';
                }
                return false;
            };

            let userLevel = 1; // OP
            if (check(personRole.Rol_VP)) userLevel = 3;
            else if (check(personRole.Rol_VOP)) userLevel = 2;
            else if (check(personRole.Rol_WV) || check(personRole.Rol_IV)) userLevel = 3;

            return userLevel >= riskLevel;
        }
        return true;
    };

    const handleNext = async () => {
        if (!canProceed()) {
            alert("Vul alle verplichte velden in en zorg dat alle foto's een naam hebben.");
            return;
        }
        if (currentStep < STEPS.length - 1) {
            setCurrentStep(currentStep + 1);
            window.scrollTo(0, 0);
        }
    };

    const cleanupSession = async () => {
        // STRICT CLEANUP: Delete all session uploads if cancelling
        if (formData.sessionUploadedUrls && formData.sessionUploadedUrls.length > 0) {
            try {
                const { dataService } = await import('../services/dataService');

                await Promise.all(formData.sessionUploadedUrls.map(url =>
                    dataService.deleteImage(url).catch(e => console.warn(`Failed to delete ${url}`, e))
                ));
            } catch (err) {
                console.error("Cleanup failed:", err);
            }
        } else {
        }
    };

    // Warn user if they try to leave with pending uploads
    React.useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (formData.sessionUploadedUrls && formData.sessionUploadedUrls.length > 0) {
                e.preventDefault();
                e.returnValue = ''; // Chrome requires returnValue to be set
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [formData.sessionUploadedUrls]);

    const handleReset = async () => {
        if (confirm('Weet je zeker dat je een nieuw werkplan wilt starten? Alle niet-opgeslagen gegevens (inclusief foto\'s) gaan verloren.')) {
            // Trigger Cleanup BEFORE resetting state
            await cleanupSession();

            setFormData({
                location: locationName || '',
                ultimoNr: '',
                date: new Date().toISOString().split('T')[0],
                description: '',
                goal: '',
                hall: '',
                line: '',
                subline: '',
                component: '',
                task: '',
                safetyQuestions: {},
                lotoPhotos: [],
                werkvoorbereider: '',
                werkverantwoordelijke: '',
                monteur1: '',
                monteur2: '',
                risks: [],
                tools: [],
                pbms: [],
                assignedUserId: '',
            });
            setCurrentStep(0);
            setNewPhotoUploaded(false);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            window.scrollTo(0, 0);
        }
    };

    // --- Dynamic Data for Wizard ---
    // If formData.location changes, we must fetch the correct library/hierarchy/users for THAT location.
    const [dynamicWizardData, setDynamicWizardData] = useState(data);

    // Sync with upstream data updates (e.g. initial async load from App.jsx)
    React.useEffect(() => {
        if (data) {
            setDynamicWizardData(prev => ({
                ...prev,
                ...data,
                // Ensure hierarchy is replaced if data has it (and it's not empty)
                hierarchy: (data.hierarchy && Object.keys(data.hierarchy).length > 0) ? data.hierarchy : prev.hierarchy,
                onderdeel_tasks: (data.onderdeel_tasks && Object.keys(data.onderdeel_tasks).length > 0) ? data.onderdeel_tasks : prev.onderdeel_tasks,
                // Only override if meaningful data comes in, to prevent wiping local state if parent sends partial update?
                // Actually, parent 'data' from App.jsx is usually the Source of Truth on load. 
                // We should probably trust it fully if it matches the location.
            }));

            // Stronger reset if location matches
            if (data.hierarchy && Object.keys(data.hierarchy).length > 0) {
                setDynamicWizardData(data);
            }
        }
    }, [data]);

    React.useEffect(() => {
        const fetchWizardData = async () => {
            // Only fetch if location is selected and differs from base data (or just always fetch to be safe)
            if (!formData.location) return;

            try {
                const { dataService } = await import('../services/dataService');
                const locName = formData.location;

                // Parallel fetch for speed
                const [loc, library, users] = await Promise.all([
                    dataService.getLocation(locName),
                    dataService.getLocationLibrary(locName), // Fetch LOCAL library explicitly!
                    dataService.getAllUsers()
                ]);


                setDynamicWizardData(prev => {
                    const next = {
                        ...prev, // Start with previous state to preserve globals not fetched here
                        ...loc, // Merges scalar props and replaces top-level objects if present

                        // Explicitly replace complex objects to avoid stale deep merge
                        hierarchy: loc?.hierarchy || {},

                        // FIX: Merge Global Base (data) + Local Library (library)
                        // This prevents local library from "wiping" the global components.
                        onderdeel_tasks: {
                            ...(data?.onderdeel_tasks || {}),
                            ...(library?.onderdeel_tasks || {})
                        },
                        task_metadata: {
                            ...(data?.task_metadata || {}),
                            ...(library?.task_metadata || {})
                        },

                        // Preserve globals (these are constant across locations)
                        global_tools: prev.global_tools,
                        global_ppes: prev.global_ppes,
                        mechanic_checks_library: prev.mechanic_checks_library,
                        config_waste: prev.config_waste,

                        // Update Users & Personnel
                        users: users,
                        personnel: loc?.personnel || []
                    };
                    return next;
                });
            } catch (err) {
                console.error("Failed to fetch dynamic wizard data:", err);
            }
        };

        fetchWizardData();
    }, [formData.location]);

    // Calculate combined data (merged hierarchy + library + conditional logic)
    // MOVED HERE: Must be after dynamicWizardData is defined!
    const combinedData = useMemo(() => {
        return getCombinedData(dynamicWizardData, formData);
    }, [formData, dynamicWizardData]);


    return (
        <div className="max-w-7xl mx-auto w-full">
            {/* Stepper */}
            <nav aria-label="Voortgang" className="mb-4 md:mb-8 relative no-print px-1 md:px-4">
                <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-slate-700/50 -z-10" aria-hidden="true" />
                <ol className="flex justify-between items-center w-full m-0 p-0 list-none">
                    {STEPS.map((step, idx) => {
                        const isActive = idx === currentStep;
                        const isCompleted = idx < currentStep;
                        return (
                            <li key={idx} className="flex flex-col items-center bg-slate-900/80 backdrop-blur px-0.5 md:px-2 rounded-lg" aria-current={isActive ? 'step' : undefined}>
                                <div
                                    className={`w-5 h-5 md:w-10 md:h-10 rounded-full flex items-center justify-center font-bold text-[8px] md:text-sm transition-all shadow-lg ${isActive ? 'bg-blue-500 text-white shadow-blue-500/30 scale-110' :
                                        isCompleted ? 'bg-green-500 text-white shadow-green-500/30' : 'bg-slate-800 text-slate-500 border border-slate-700'
                                        }`}
                                    aria-label={`Stap ${idx + 1}: ${step.title} ${isCompleted ? '(Voltooid)' : isActive ? '(Actief)' : ''}`}
                                >
                                    {isCompleted ? '✓' : idx + 1}
                                </div>
                                <span className={`step-title-mobile-hidden hidden md:block text-xs mt-2 font-medium tracking-wide text-center ${isActive ? 'text-blue-400' : 'text-slate-500'}`}>
                                    {step.title}
                                </span>
                            </li>
                        );
                    })}
                </ol>
            </nav>

            <div className="card glass-panel mb-8 min-h-[400px] fade-in">
                <StepComponent
                    data={dynamicWizardData}
                    value={formData}
                    onChange={setFormData}
                    combinedData={combinedData}
                    lotoContext={combinedData}
                    currentUser={currentUser}
                    setNewPhotoUploaded={setNewPhotoUploaded}
                    newPhotoUploaded={newPhotoUploaded}
                    onNext={handleNext}
                    onReset={handleReset}
                    availableLocations={availableLocations}
                    currentLocation={locationName}
                    onUpdateLocation={onUpdateLocation}
                    onSubmitSuccess={() => {
                        // CRITICAL: Clear session uploads list locally so cleanupSession/onReset doesn't delete them.
                        // We set it to empty array immediately.
                        setFormData(prev => ({ ...prev, sessionUploadedUrls: [] }));

                        // Then trigger the parent success handler (which clears persistent state)
                        if (onSubmitSuccess) onSubmitSuccess();
                    }}
                />
            </div>

            {/* Navigation (Sticky Footer) */}
            <div
                className="fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-700 px-4 py-4 md:px-6 md:py-6 flex justify-between items-center no-print shadow-[0_-5px_20px_rgba(0,0,0,0.5)]"
                style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999, display: 'flex' }}
            >
                <div className="max-w-7xl mx-auto w-full flex justify-between px-4">
                    <button
                        onClick={handleBack}
                        disabled={currentStep === 0}
                        className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Vorige
                    </button>

                    {currentStep < STEPS.length - 1 && (
                        <button
                            onClick={handleNext}
                            disabled={!canProceed()}
                            className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-900/20"
                        >
                            Volgende
                        </button>
                    )}
                </div>
            </div>

            {/* Spacer for sticky footer */}
            <div className="h-24"></div>
        </div>
    );
}
